/**
 * SEO & Performance Configuration for BM Maler A/S
 * Prepares all metadata, canonical links, social tags, and structured data
 * ready for production deployment at https://bmmaler.dk.
 */

export const LIVE_DOMAIN = 'https://bmmaler.dk';
export const IS_STAGING = typeof window !== 'undefined' && !window.location.hostname.includes('bmmaler.dk');

/**
 * Cloudflare Image Optimization Helper
 * Transforms assets.auxodev.dk original URLs to responsive, WebP/AVIF optimized variants.
 */
export function getOptimizedImageUrl(originalUrl, { width = 800, quality = 80, format = 'auto' } = {}) {
  if (!originalUrl || typeof originalUrl !== 'string') return originalUrl;
  if (!originalUrl.startsWith('https://assets.auxodev.dk/')) return originalUrl;
  if (originalUrl.includes('/cdn-cgi/image/')) return originalUrl;

  const urlObj = new URL(originalUrl);
  const pathname = urlObj.pathname;
  return `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=${format}${pathname}`;
}

/**
 * Page-by-page SEO Metadata Dictionary
 */
export const SEO_PAGES = {
  '/': {
    title: 'BM Maler | malerarbejde i særklasse - kvalitet siden 1957',
    description: 'Vælg BM Maler, din maler i Vejle siden 1957. Få et flot og holdbart resultat med grundigt forarbejde, moderne teknik og stolte håndværkstraditioner.',
    canonical: '/',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/a4ab1f20-dc6d-4e64-b7ae-1e79f8e81f16.jpg',
  },
  '/maler-vejle': {
    title: 'Maler i Vejle og Trekantsområdet | 70 års erfaring | BM Maler',
    description: 'BM Maler har siden 1957 udført professionelt malerarbejde i Vejle, der forener stolte håndværkstraditioner med moderne teknikker. Ring nu på 75 69 59 00',
    canonical: '/maler-vejle',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/a4ab1f20-dc6d-4e64-b7ae-1e79f8e81f16.jpg',
    faq: [
      {
        question: 'Hvad laver en maler?',
        answer: 'En maler arbejder med behandling og vedligeholdelse af forskellige overflader i både private hjem og erhvervsejendomme. Det kan blandt andet være maling af vægge, lofter, træværk og facader samt spartling, opsætning af filt, tapetsering og andre former for overfladebehandling.',
      },
      {
        question: 'Hvad koster en maler?',
        answer: 'Prisen på en maler afhænger blandt andet af opgavens størrelse, overfladernes stand, materialevalg og det nødvendige forarbejde. Vi dækker et stort geografisk område og kører ud til kunder i hele lokalområdet, og derfor bliver der pålagt kørsel på alle vores opgaver. Den samlede pris afhænger dermed af både selve malerarbejdet og opgavens placering.',
      },
      {
        question: 'Hvad koster en maler i timen eller pr. m²?',
        answer: 'Prisen på malerarbejde kan opgøres på forskellige måder, men både timepris og pris pr. m² afhænger af opgavens karakter. Overfladens stand, antallet af behandlinger og det nødvendige forarbejde har blandt andet betydning for den samlede pris. En overflade, der kræver omfattende spartling eller andet forarbejde, vil eksempelvis tage længere tid at behandle end en allerede klargjort overflade.',
      },
      {
        question: 'Hvordan finder man en god maler?',
        answer: 'Når man vælger maler, er det relevant at se på erfaring, faglighed og kvaliteten af det arbejde, der udføres. Hos BM Maler består teamet af faglærte malersvende og talentfulde lærlinge, og virksomheden har arbejdet med malerfaget siden 1957.',
      },
      {
        question: 'Arbejder BM Maler i Vejle og Trekantsområdet?',
        answer: 'BM Maler har base i Vejle og har kunder i hele Trekantsområdet. Virksomheden udfører malerarbejde for både private og erhverv og håndterer både mindre og større opgaver.',
      },
    ],
  },
  '/malermester-trekantsomraadet': {
    title: 'Malermester i Trekantsområdet | 70 års erfaring | BM Maler',
    description: 'BM Maler har siden 1957 udført professionelt malerarbejde i Vejle og hele Trekantsområdet. Forener stolte håndværkstraditioner med moderne teknikker. Ring 75 69 59 00',
    canonical: '/malermester-trekantsomraadet',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/a4ab1f20-dc6d-4e64-b7ae-1e79f8e81f16.jpg',
    faq: [
      {
        question: 'Hvad laver en malermester?',
        answer: 'En malermester arbejder med malerfaget og kan stå for både mindre og større maleropgaver for private og erhverv. Det kan blandt andet være maling, spartling, opsætning af filt og tapet samt forskellige former for indendørs og udendørs malerarbejde.',
      },
      {
        question: 'Hvad er forskellen på en maler og en malermester?',
        answer: 'En maler udfører det praktiske malerarbejde, mens en malermester typisk har et bredere ansvar for faglighed, planlægning og kvalitet i forbindelse med maleropgaver. Hos BM Maler består teamet af faglærte malersvende og lærlinge, og virksomheden har arbejdet med malerfaget siden 1957.',
      },
      {
        question: 'Hvad koster en malermester i timen?',
        answer: 'Prisen på en malermester afhænger blandt andet af opgavens størrelse, kompleksitet, materialer og det nødvendige forarbejde. Der kan derfor være forskel på den samlede pris fra opgave til opgave. Da vi dækker et stort geografisk område og kører ud til kunder i hele lokalområdet, bliver der pålagt kørsel på alle vores opgaver.',
      },
      {
        question: 'Hvordan finder man en god malermester?',
        answer: 'Når man vælger malermester, er det relevant at se på faglighed, erfaring og kvaliteten af det arbejde, der udføres. Hos BM Maler har vi siden 1957 arbejdet med fokus på godt håndværk, ordentlighed og høj faglighed.',
      },
      {
        question: 'Kan en malermester hjælpe både private og erhverv?',
        answer: 'Ja. BM Maler udfører malerarbejde for både private og erhverv. Opgaverne spænder fra private boliger til større erhvervsprojekter og renoveringer, hvor malerarbejdet kan indgå som en del af et større bygge- eller renoveringsprojekt.',
      },
    ],
  },
  '/malermester-trekantsområdet': {
    title: 'Malermester i Trekantsområdet | 70 års erfaring | BM Maler',
    description: 'BM Maler har siden 1957 udført professionelt malerarbejde i Vejle og hele Trekantsområdet. Forener stolte håndværkstraditioner med moderne teknikker. Ring 75 69 59 00',
    canonical: '/malermester-trekantsomraadet',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/a4ab1f20-dc6d-4e64-b7ae-1e79f8e81f16.jpg',
    faq: [
      {
        question: 'Hvad laver en malermester?',
        answer: 'En malermester arbejder med malerfaget og kan stå for både mindre og større maleropgaver for private og erhverv. Det kan blandt andet være maling, spartling, opsætning af filt og tapet samt forskellige former for indendørs og udendørs malerarbejde.',
      },
      {
        question: 'Hvad er forskellen på en maler og en malermester?',
        answer: 'En maler udfører det praktiske malerarbejde, mens en malermester typisk har et bredere ansvar for faglighed, planlægning og kvalitet i forbindelse med maleropgaver. Hos BM Maler består teamet af faglærte malersvende og lærlinge, og virksomheden har arbejdet med malerfaget siden 1957.',
      },
      {
        question: 'Hvad koster en malermester i timen?',
        answer: 'Prisen på en malermester afhænger blandt andet af opgavens størrelse, kompleksitet, materialer og det nødvendige forarbejde. Der kan derfor være forskel på den samlede pris fra opgave til opgave. Da vi dækker et stort geografisk område og kører ud til kunder i hele lokalområdet, bliver der pålagt kørsel på alle vores opgaver.',
      },
      {
        question: 'Hvordan finder man en god malermester?',
        answer: 'Når man vælger malermester, er det relevant at se på faglighed, erfaring og kvaliteten af det arbejde, der udføres. Hos BM Maler har vi siden 1957 arbejdet med fokus på godt håndværk, ordentlighed og høj faglighed.',
      },
      {
        question: 'Kan en malermester hjælpe både private og erhverv?',
        answer: 'Ja. BM Maler udfører malerarbejde for både private og erhverv. Opgaverne spænder fra private boliger til større erhvervsprojekter og renoveringer, hvor malerarbejdet kan indgå som en del af et større bygge- eller renoveringsprojekt.',
      },
    ],
  },
  '/profil': {
    title: 'Om BM Maler A/S | Knap 70 års håndværkserfaring & kvalitet',
    description: 'Læs om BM Maler A/S. Siden 1957 har vores faglærte malersvende og lærlinge leveret ordentligt malerarbejde til private og erhverv i Vejle.',
    canonical: '/profil',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/image.png',
  },
  '/om-os': {
    title: 'Om BM Maler A/S | Knap 70 års håndværkserfaring & kvalitet',
    description: 'Læs om BM Maler A/S. Siden 1957 har vores faglærte malersvende og lærlinge leveret ordentligt malerarbejde til private og erhverv i Vejle.',
    canonical: '/profil',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/image.png',
  },
  '/ydelser': {
    title: 'Ydelser & Malerarbejde | Professionel Malermester | BM Maler A/S',
    description: 'Se alle vores ydelser inden for malerarbejde: maling af vægge, spartling, opsætning af filt, tapet, erhvervsbyggeri, epoxymaling og facademaling i Vejle og omegn.',
    canonical: '/ydelser',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/fb1ba773-6f6e-4a2c-ad28-a29b362c6eb1.jpg',
  },
  '/ydelser/maling-af-vaegge': {
    title: 'Maling af vægge & lofter | Professionelt indendørs malerarbejde | BM Maler',
    description: 'Få perfekt malede vægge og lofter. BM Maler A/S sikrer knivskarp finish, præcision og harmoniske farvevalg til dit hjem eller erhverv.',
    canonical: '/ydelser/maling-af-vaegge',
    ogType: 'article',
    serviceName: 'Maling af vægge og lofter',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/fb1ba773-6f6e-4a2c-ad28-a29b362c6eb1.jpg',
  },
  '/ydelser/spartling-af-vaegge': {
    title: 'Spartling af vægge | Fuldspartling & klargøring i Vejle | BM Maler',
    description: 'Professionel fuldspartling og slibning for helt jævne og glatte vægge. Vi klargør dine overflader til filt, tapet eller maling.',
    canonical: '/ydelser/spartling-af-vaegge',
    ogType: 'article',
    serviceName: 'Spartling af vægge og fuldspartling',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/b717f329-9b38-47ab-b4ad-ddf4fd8d982c.jpg',
  },
  '/ydelser/opsaetning-af-filt': {
    title: 'Opsætning af filt & glasvæv | Perfekt glat finish | BM Maler',
    description: 'Professionel opsætning af glasfilt og glasvæv. Få stærke, ensartede og revnefrie overflader på dine vægge i Vejle og omegn.',
    canonical: '/ydelser/opsaetning-af-filt',
    ogType: 'article',
    serviceName: 'Opsætning af filt og glasvæv',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/95374d40-7b43-4332-b90d-847f8df2ef5f.jpg',
  },
  '/ydelser/tapet': {
    title: 'Opsætning af tapet | Mønstertapet & vægbeklædning | BM Maler',
    description: 'Præcis tapetsering af moderne mønstre, savsmuldstapet og eksklusiv vægbeklædning udført af erfarne faglærte malere.',
    canonical: '/ydelser/tapet',
    ogType: 'article',
    serviceName: 'Opsætning af tapet og vægbeklædning',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/156a77a4-90b0-49bb-b5ea-e65043a3783d.jpg',
  },
  '/ydelser/erhvervs-og-nybyggeri': {
    title: 'Erhvervs- og nybyggeri malerarbejde | Professionelle entrepriser | BM Maler',
    description: 'BM Maler A/S udfører malerarbejde til erhverv, kontorer og nybyggeri. Pålidelig tidsplan, høj kapacitet og uovertruffen kvalitet.',
    canonical: '/ydelser/erhvervs-og-nybyggeri',
    ogType: 'article',
    serviceName: 'Erhvervs- og nybyggeri malerarbejde',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/d8eccbb4-67a8-4abe-b735-6e29e1d2053c.jpg',
  },
  '/ydelser/erhverv': {
    title: 'Erhvervsmaler i Vejle & Trekantsområdet | BM Maler A/S',
    description: 'Erfaren erhvervsmaler til kontordomiciler, butikker, produktionslokaler og udlejningsejendomme. Stor kapacitet og overholdte deadlines.',
    canonical: '/ydelser/erhverv',
    ogType: 'article',
    serviceName: 'Erhvervsmaler og vedligeholdelsesaftaler',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/00d1f8e8-ea1e-4beb-b2b3-8afe34ff51d1.jpg',
  },
  '/ydelser/private': {
    title: 'Malerarbejde for private boligejere | Vejle & omegn | BM Maler',
    description: 'Kvalitetsbevidst malerarbejde til private hjem. Vi værner om detaljen, god dialog og et sundt arbejdsmiljø. Få et uforpligtende tilbud.',
    canonical: '/ydelser/private',
    ogType: 'article',
    serviceName: 'Malerarbejde for private boligejere',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/65edb7cb-b2dd-4556-9798-241f24eff286.jpg',
  },
  '/ydelser/epoxymaling': {
    title: 'Epoxymaling i Vejle & Trekantsområdet | Slidstærke gulve | BM Maler',
    description: 'Professionel påføring af epoxymaling til garager, værksteder, industrikøkkener og vådrum. Slidstærkt, kemikalieresistent og fugefrit.',
    canonical: '/ydelser/epoxymaling',
    ogType: 'article',
    serviceName: 'Epoxymaling og overfladebehandling',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/51acb229-1661-4562-901c-2f9c04824c0e.jpg',
  },
  '/ydelser/vedligeholdelsesarbejde': {
    title: 'Vedligeholdelse af ejendomme & træværk | BM Maler A/S',
    description: 'Forlæng levetiden på din ejendoms træværk, vinduer og facader med løbende professionelt vedligeholdelsesarbejde.',
    canonical: '/ydelser/vedligeholdelsesarbejde',
    ogType: 'article',
    serviceName: 'Løbende vedligeholdelsesarbejde',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/95184475-f738-42ad-8d8f-fbf41b0cdb58.jpg',
  },
  '/ydelser/udvendigt-malerarbejde': {
    title: 'Udvendigt malerarbejde | Facademaling & træværk i Vejle | BM Maler',
    description: 'Beskyt din bolig mod vind og vejr. Professionel facademaling og behandling af udvendigt træværk udført af BM Maler A/S.',
    canonical: '/ydelser/udvendigt-malerarbejde',
    ogType: 'article',
    serviceName: 'Udvendigt malerarbejde og facademaling',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/96ca710d-a5d8-405c-82da-48301c3b7bcc.jpg',
  },
  '/galleri': {
    title: 'Galleri & Billeder | Se eksempler på vores malerarbejde | BM Maler',
    description: 'Udforsk vores galleri med billeder fra udførte malerprojekter. Se detaljer, finish og håndværk fra BM Maler A/S.',
    canonical: '/galleri',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/11587-bm-maler-021_1.jpg',
  },
  '/kundecases': {
    title: 'Kundecases & Referencer | Erhverv og private | BM Maler A/S',
    description: 'Læs hvordan BM Maler A/S løser maleropgaver for bl.a. NORMAL, Formula (Ferrari/Maserati) og Wine Mage i Vejle og omegn.',
    canonical: '/kundecases',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/030e09c4-7cdf-4f40-8ce7-423cd9c2bb58.jpg',
  },
  '/normal': {
    title: 'NORMAL Domicil Case | Erhvervsmaling i Skanderborg | BM Maler',
    description: 'BM Maler A/S står for løbende vedligeholdelse af NORMALS 3.600 kvm store kontordomicil i Skanderborg. Læs om partnerskabet.',
    canonical: '/normal',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/indendoers-malerarbejde-normal.jpg',
    caseName: 'NORMAL Domicil, Skanderborg',
  },
  '/wine-mage': {
    title: 'Wine Mage Showroom Case | Unikt malerhåndværk i Vejle | BM Maler',
    description: 'Rustikt og eksklusivt showroom for Wine Mage i My Garage, Vejle, udført med sans for detaljer af BM Maler A/S.',
    canonical: '/wine-mage',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/wine-mage-vejle-bm-maler.jpg',
    caseName: 'Wine Mage Showroom, My Garage Vejle',
  },
  '/formula': {
    title: 'Formula Showroom Case | High-end erhvervsmaling i Vejle | BM Maler',
    description: 'Eksklusivt mørkt og elegant showroom for Ferrari og Maserati i Vejle malet af BM Maler A/S med stor præcision.',
    canonical: '/formula',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/b06a8ee4-f25d-4358-b8b8-996c0a620738.jpg',
    caseName: 'Formula Showroom, Ferrari & Maserati Vejle',
  },
  '/laerling-i-bm-maler': {
    title: 'Lærling hos BM Maler A/S | Uddannelse til faglært malersvend',
    description: 'Bliv malerlærling i en traditionsrig virksomhed med stærkt sammenhold, faglig stolthed og grundig oplæring i Vejle.',
    canonical: '/laerling-i-bm-maler',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/ab224ef7-4a43-481f-b69f-f5c70757ff99.jpg',
  },
  '/super-svende-soeges-til-bm-maler-i-vejle': {
    title: 'Ledige stillinger | Bliv malersvend hos BM Maler A/S i Vejle',
    description: 'Søger du nyt malerjob? BM Maler A/S søger dygtige faglærte malersvende til spændende projekter i Vejle og omegn.',
    canonical: '/super-svende-soeges-til-bm-maler-i-vejle',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/aea21f0a-1021-4316-abe0-f488cfc2464c.jpg',
  },
  '/hvad-koster-en-maler': {
    title: 'Hvad koster en maler? | Priser & gennemskuelige tilbud | BM Maler',
    description: 'Få overblik over hvad malerarbejde koster. BM Maler A/S tilbyder uforpligtende tilbud og gennemskuelige priser i Vejle og Trekantsområdet.',
    canonical: '/hvad-koster-en-maler',
    ogType: 'article',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/ff77cde6-33b3-48cb-8b13-3071df9b4522.jpg',
    faq: [
      {
        question: 'Hvad koster en maler i timen eller pr. kvadratmeter?',
        answer: 'Prisen på malerarbejde afhænger af flere faktorer såsom opgavens type, overfladernes stand, forarbejde (fx spartling og filt) samt materialevalg. BM Maler udregner altid et individuelt og gennemskueligt tilbud baseret på dit konkrete projekt.',
      },
      {
        question: 'Bliver der pålagt kørsel på opgaverne?',
        answer: 'Ja, da vi dækker et stort geografisk område og kører ud til kunder i hele Vejle, Horsens, Juelsminde, Hedensted, Grindsted, Billund, Fredericia og Trekantsområdet, bliver der pålagt kørsel for at dække transport og logistik.',
      },
      {
        question: 'Hvorfor er et godt forarbejde afgørende for prisen og resultatet?',
        answer: 'Grundigt forarbejde med rengøring, spartling, slibning og eventuel opsætning af filt sikrer, at malingen hæfter optimalt og giver en ensartet, holdbar overflade. Uden ordentligt forarbejde holder resultatet ikke i længden.',
      },
      {
        question: 'Kan jeg få et uforpligtende tilbud på min maleropgave?',
        answer: 'Ja, vi giver altid gerne et uforpligtende tilbud og faglig rådgivning. Kontakt os på telefon 75 69 59 00 eller e-mail bm@bmmaler.dk.',
      },
    ],
  },
  '/kontakt': {
    title: 'Kontakt BM Maler A/S | Få et uforpligtende tilbud på malerarbejde',
    description: 'Ring på 75 69 59 00 eller send en besked til BM Maler A/S på Håndværkervej 3A i Vejle Øst. Vi giver gerne et godt og uforpligtende tilbud.',
    canonical: '/kontakt',
    ogType: 'website',
    ogImage: 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/f074c4a2-e267-415d-b35e-cc61789452c9.jpg',
  },
};

/**
 * Generate Structured Data (JSON-LD) for BM Maler A/S
 */
export function getStructuredData(pathname, customTitle, customDescription, customFaq = null) {
  const currentPath = pathname || '/';
  const liveUrl = `${LIVE_DOMAIN}${currentPath}`;
  const pageMeta = SEO_PAGES[currentPath] || {};

  // Core Service Catalog for OfferCatalog schema
  const servicesCatalog = [
    { name: 'Maling af vægge og lofter', path: '/ydelser/maling-af-vaegge', description: 'Professionelt indendørs malerarbejde med knivskarp finish og harmoniske farvevalg.' },
    { name: 'Spartling af vægge', path: '/ydelser/spartling-af-vaegge', description: 'Professionel fuldspartling og slibning for fuldstændigt jævne og glatte vægge.' },
    { name: 'Opsætning af filt og glasvæv', path: '/ydelser/opsaetning-af-filt', description: 'Opsætning af glasfilt og glasvæv for stærke, ensartede og revnefrie overflader.' },
    { name: 'Opsætning af tapet', path: '/ydelser/tapet', description: 'Præcis tapetsering af mønstertapet, vægbeklædning og savsmuldstapet.' },
    { name: 'Erhvervs- og nybyggeri malerarbejde', path: '/ydelser/erhvervs-og-nybyggeri', description: 'Malerentrepriser til erhvervsbyggeri, kontorer og nybyggeri med overholdte deadlines.' },
    { name: 'Erhvervsmaler', path: '/ydelser/erhverv', description: 'Malerarbejde og faste vedligeholdelsesaftaler til virksomheder og udlejningsejendomme.' },
    { name: 'Malerarbejde for private', path: '/ydelser/private', description: 'Kvalitetsmalerarbejde til private boligejere med fokus på finish og sundt arbejdsmiljø.' },
    { name: 'Epoxymaling', path: '/ydelser/epoxymaling', description: 'Slidstærk og kemikalieresistent epoxymaling til garager, værksteder og industri.' },
    { name: 'Udvendigt malerarbejde', path: '/ydelser/udvendigt-malerarbejde', description: 'Facademaling og udvendig behandling af træværk, gavle, døre og vinduer.' },
    { name: 'Vedligeholdelsesarbejde', path: '/ydelser/vedligeholdelsesarbejde', description: 'Løbende vedligeholdelse af ejendomme, skoler, boligforeninger og private boliger.' },
  ];

  // Base LocalBusiness / HousePainter schema with E-E-A-T attributes
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'HousePainter'],
    '@id': `${LIVE_DOMAIN}/#organization`,
    name: 'BM Maler A/S',
    legalName: 'BM MALER A/S',
    foundingDate: '1957',
    url: LIVE_DOMAIN,
    logo: {
      '@type': 'ImageObject',
      '@id': `${LIVE_DOMAIN}/#logo`,
      url: getOptimizedImageUrl('https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/97f3f97c-e329-43a2-8e51-2f2cfda5a57e.png', { width: 300 }),
      caption: 'BM Maler A/S Logo',
      width: 300,
      height: 60,
    },
    image: {
      '@type': 'ImageObject',
      '@id': `${LIVE_DOMAIN}/#primaryimage`,
      url: getOptimizedImageUrl(pageMeta.ogImage || 'https://assets.auxodev.dk/assets/b3b17412-91e4-45b1-be58-3b3c80af2fb2/a4ab1f20-dc6d-4e64-b7ae-1e79f8e81f16.jpg', { width: 1200 }),
      width: 1200,
      height: 630,
      caption: 'BM Maler A/S Malerarbejde',
    },
    telephone: '+4575695900',
    email: 'bm@bmmaler.dk',
    vatID: 'DK25999193',
    priceRange: '$$',
    currenciesAccepted: 'DKK',
    paymentAccepted: 'Faktura, Bankoverførsel, MobilePay, Kontant',
    knowsAbout: [
      'Malerarbejde',
      'Fuldspartling',
      'Spartling af vægge',
      'Opsætning af filt',
      'Opsætning af glasvæv',
      'Tapetsering',
      'Erhvervsmaling',
      'Facademaling',
      'Udvendigt malerarbejde',
      'Epoxymaling',
      'Træbeskyttelse',
      'Vedligeholdelsesarbejde',
    ],
    memberOf: [
      {
        '@type': 'Organization',
        name: 'Danske Malermestre',
        url: 'https://malermestre.dk',
      },
      {
        '@type': 'Organization',
        name: 'Byg Garanti',
        url: 'https://www.byggaranti.dk',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Maler- og overfladebehandlingsydelser',
      itemListElement: servicesCatalog.map((service, index) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: service.name,
          url: `${LIVE_DOMAIN}${service.path}`,
          description: service.description,
        },
      })),
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Håndværkervej 3A',
      addressLocality: 'Vejle Øst',
      postalCode: '7120',
      addressCountry: 'DK',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 55.7092,
      longitude: 9.5841,
    },
    hasMap: 'https://www.google.com/maps?q=BM+Maler+A/S,+H%C3%A5ndv%C3%A6rkervej+3A,+7120+Vejle+%C3%98st',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+4575695900',
        contactType: 'customer service',
        email: 'bm@bmmaler.dk',
        areaServed: 'DK',
        availableLanguage: ['da', 'en'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '07:00',
          closes: '16:00',
        },
      },
    ],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '07:00',
        closes: '16:00',
      },
    ],
    areaServed: currentPath.includes('trekantsomraad') || currentPath.includes('trekantsområd')
      ? [
          { '@type': 'AdministrativeArea', name: 'Trekantsområdet' },
          { '@type': 'City', name: 'Vejle' },
          { '@type': 'City', name: 'Kolding' },
          { '@type': 'City', name: 'Fredericia' },
          { '@type': 'City', name: 'Middelfart' },
          { '@type': 'City', name: 'Horsens' },
          { '@type': 'City', name: 'Hedensted' },
          { '@type': 'City', name: 'Billund' },
          { '@type': 'City', name: 'Vejen' },
        ]
      : [
          { '@type': 'City', name: 'Vejle' },
          { '@type': 'AdministrativeArea', name: 'Trekantsområdet' },
          { '@type': 'City', name: 'Kolding' },
          { '@type': 'City', name: 'Fredericia' },
          { '@type': 'City', name: 'Horsens' },
          { '@type': 'City', name: 'Hedensted' },
          { '@type': 'City', name: 'Juelsminde' },
        ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      bestRating: '5',
      worstRating: '1',
      ratingCount: '52',
    },
    review: [
      {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
        author: {
          '@type': 'Person',
          name: 'Thomas Gravesen',
        },
        datePublished: '2024-03-12',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Vejle Padelcenter. Vi har brugt BM Maler til vores projekt. Det har været en god oplevelse med god rådgivning omkring farvevalg og materialer. Arbejdet er udført til tiden og i høj kvalitet. Kan varmt anbefale BM Maler.',
        publisher: {
          '@type': 'Organization',
          name: 'Google',
        },
      },
      {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
        author: {
          '@type': 'Person',
          name: 'Rasmus Egsgaard',
        },
        datePublished: '2024-05-18',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'En super god service og rigtig godt arbejde. Det var en flink og dygtig maler som udførte arbejdet og det blev gjort rigtig godt og med et super flot resultat. Har brugt BM Maler flere gange og hver gang meget tilfreds. Så vil klart anbefale BM Maler.',
        publisher: {
          '@type': 'Organization',
          name: 'Google',
        },
      },
      {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
        author: {
          '@type': 'Person',
          name: 'Vibeke Dissing',
        },
        datePublished: '2024-07-22',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Super flot arbejde! Nu står huset lyst og skarpt. Samarbejdet var helt perfekt.',
        publisher: {
          '@type': 'Organization',
          name: 'Google',
        },
      },
      {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
        author: {
          '@type': 'Person',
          name: 'Flemming Lund',
        },
        datePublished: '2024-08-30',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Fik udført spartling og opsætning af filt i stuen med kort varsel og til aftalt pris.',
        publisher: {
          '@type': 'Organization',
          name: 'Google',
        },
      },
      {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
        author: {
          '@type': 'Person',
          name: 'Benjamin Madsen',
        },
        datePublished: '2024-09-14',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Super godt og effektivt arbejde :-)',
        publisher: {
          '@type': 'Organization',
          name: 'Google',
        },
      },
      {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
        author: {
          '@type': 'Person',
          name: 'Kristine Lingren Larsen Albertsen',
        },
        datePublished: '2024-10-02',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Kun rosende ord herfra ~ ALT malearbejde og tapetsering, 100 % professionelt udført!',
        publisher: {
          '@type': 'Organization',
          name: 'Facebook',
        },
      },
      {
        '@type': 'Review',
        itemReviewed: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
        author: {
          '@type': 'Person',
          name: 'Ole Christiansen',
        },
        datePublished: '2024-10-18',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
        },
        reviewBody: 'Har brugt BM flere gange og altid top godt arbejde',
        publisher: {
          '@type': 'Organization',
          name: 'Facebook',
        },
      },
    ],
    sameAs: [
      'https://www.facebook.com/bmmaler/',
      'https://www.instagram.com/bmmaler/',
    ],
  };

  // WebSite schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${LIVE_DOMAIN}/#website`,
    url: LIVE_DOMAIN,
    name: 'BM Maler A/S',
    alternateName: ['BM Maler', 'BM Maler Vejle'],
    description: 'BM Maler A/S – Professionelt malerarbejde og malermester i Vejle og Trekantsområdet siden 1957.',
    inLanguage: 'da-DK',
    publisher: {
      '@id': `${LIVE_DOMAIN}/#organization`,
    },
  };

  // WebPage schema enriched with speakable, inLanguage, and Wikidata entity mappings
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': currentPath === '/kontakt'
      ? ['WebPage', 'ContactPage']
      : (currentPath === '/profil' || currentPath === '/om-os'
          ? ['WebPage', 'AboutPage']
          : 'WebPage'),
    '@id': `${liveUrl}#webpage`,
    url: liveUrl,
    name: customTitle || pageMeta.title || 'BM Maler A/S',
    description: customDescription || pageMeta.description || '',
    inLanguage: 'da-DK',
    mainEntityOfPage: liveUrl,
    isPartOf: {
      '@id': `${LIVE_DOMAIN}/#website`,
    },
    about: [
      {
        '@type': 'Thing',
        name: 'House painting and decorating',
        sameAs: 'https://www.wikidata.org/wiki/Q1144415',
      },
      {
        '@type': 'Place',
        name: 'Vejle',
        sameAs: 'https://www.wikidata.org/wiki/Q27119',
      },
      {
        '@type': 'Place',
        name: 'Trekantområdet',
        sameAs: 'https://www.wikidata.org/wiki/Q12339739',
      },
    ],
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'meta[name="description"]'],
    },
    ...(currentPath === '/kontakt'
      ? {
          mainEntity: {
            '@id': `${LIVE_DOMAIN}/#organization`,
          },
          potentialAction: {
            '@type': 'CommunicateAction',
            name: 'Kontakt BM Maler A/S og få et uforpligtende tilbud',
            description: 'Send en henvendelse eller ring på 75 69 59 00 for uforpligtende tilbud på malerarbejde i Vejle og Trekantsområdet.',
            target: `${liveUrl}#kontakt-formular`,
          },
        }
      : {}),
    ...(currentPath === '/profil' || currentPath === '/om-os'
      ? {
          mainEntity: {
            '@id': `${LIVE_DOMAIN}/#organization`,
          },
        }
      : {}),
  };

  const schemas = [localBusinessSchema, websiteSchema, webPageSchema];

  // Hierarchical BreadcrumbList schema
  if (currentPath !== '/') {
    const rawTitle = customTitle || pageMeta.title || currentPath.replace('/', '');
    const cleanTitle = rawTitle.split('|')[0].trim();

    if (currentPath === '/ydelser') {
      // 2 levels: Forside -> Ydelser
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Forside',
            item: LIVE_DOMAIN,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Ydelser',
            item: `${LIVE_DOMAIN}/ydelser`,
          },
        ],
      });
    } else if (currentPath.startsWith('/ydelser/')) {
      // 3 levels for services: Forside -> Ydelser -> Specific Service
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Forside',
            item: LIVE_DOMAIN,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Ydelser',
            item: `${LIVE_DOMAIN}/ydelser`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: cleanTitle,
            item: liveUrl,
          },
        ],
      });
    } else if (['/normal', '/wine-mage', '/formula'].includes(currentPath)) {
      // 3 levels for case studies: Forside -> Kundecases -> Specific Case
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Forside',
            item: LIVE_DOMAIN,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Kundecases',
            item: `${LIVE_DOMAIN}/kundecases`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: cleanTitle,
            item: liveUrl,
          },
        ],
      });
    } else {
      // Standard 2 levels
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Forside',
            item: LIVE_DOMAIN,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: cleanTitle,
            item: liveUrl,
          },
        ],
      });
    }
  }

  // Add rich Service schema for service pages
  if (currentPath.startsWith('/ydelser/')) {
    const serviceName = pageMeta.serviceName || customTitle || 'Malerarbejde';
    
    // Determine Wikidata category for specific painting services
    let serviceSameAs = 'https://www.wikidata.org/wiki/Q11629'; // Default: Painting
    if (currentPath.includes('epoxy')) {
      serviceSameAs = 'https://www.wikidata.org/wiki/Q192934'; // Epoxy
    } else if (currentPath.includes('tapet')) {
      serviceSameAs = 'https://www.wikidata.org/wiki/Q165848'; // Wallpaper
    } else if (currentPath.includes('spartling')) {
      serviceSameAs = 'https://www.wikidata.org/wiki/Q114755'; // Plasterwork
    } else if (currentPath.includes('filt')) {
      serviceSameAs = 'https://www.wikidata.org/wiki/Q1144415'; // House painter and decorator
    }

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${liveUrl}#service`,
      name: serviceName,
      serviceType: 'Malerarbejde',
      sameAs: serviceSameAs,
      provider: {
        '@id': `${LIVE_DOMAIN}/#organization`,
      },
      areaServed: [
        { '@type': 'City', name: 'Vejle' },
        { '@type': 'AdministrativeArea', name: 'Trekantsområdet' },
        { '@type': 'City', name: 'Kolding' },
        { '@type': 'City', name: 'Fredericia' },
        { '@type': 'City', name: 'Horsens' },
        { '@type': 'City', name: 'Hedensted' },
        { '@type': 'City', name: 'Juelsminde' },
      ],
      description: customDescription || pageMeta.description || '',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'DKK',
        availability: 'https://schema.org/InStock',
        url: liveUrl,
        seller: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
      },
    });
  }

  // Add rich local Service schema for geographical landing pages (/maler-vejle and /malermester-trekantsomraadet)
  if (currentPath === '/maler-vejle') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${liveUrl}#service-local`,
      name: 'Maler i Vejle',
      serviceType: 'Malerarbejde og malerservice i Vejle',
      sameAs: 'https://www.wikidata.org/wiki/Q1144415',
      provider: {
        '@id': `${LIVE_DOMAIN}/#organization`,
      },
      areaServed: [
        { '@type': 'City', name: 'Vejle', postalCode: '7100' },
        { '@type': 'City', name: 'Vejle Øst', postalCode: '7120' },
        { '@type': 'City', name: 'Bredballe' },
        { '@type': 'City', name: 'Jelling', postalCode: '7300' },
        { '@type': 'City', name: 'Børkop', postalCode: '7080' },
        { '@type': 'City', name: 'Give', postalCode: '7323' },
        { '@type': 'AdministrativeArea', name: 'Vejle Kommune' },
      ],
      description: customDescription || pageMeta.description || 'Professionelt malerarbejde i Vejle og omegn siden 1957. Få et uforpligtende tilbud på maling af vægge, lofter og facader.',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'DKK',
        availability: 'https://schema.org/InStock',
        url: liveUrl,
        seller: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
      },
    });
  } else if (currentPath.includes('trekantsomraad') || currentPath.includes('trekantsområd')) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${liveUrl}#service-local`,
      name: 'Malermester i Trekantsområdet',
      serviceType: 'Malermester og malerentrepriser i Trekantsområdet',
      sameAs: 'https://www.wikidata.org/wiki/Q1144415',
      provider: {
        '@id': `${LIVE_DOMAIN}/#organization`,
      },
      areaServed: [
        { '@type': 'AdministrativeArea', name: 'Trekantsområdet', sameAs: 'https://www.wikidata.org/wiki/Q12339739' },
        { '@type': 'City', name: 'Vejle' },
        { '@type': 'City', name: 'Kolding' },
        { '@type': 'City', name: 'Fredericia' },
        { '@type': 'City', name: 'Middelfart' },
        { '@type': 'City', name: 'Horsens' },
        { '@type': 'City', name: 'Hedensted' },
        { '@type': 'City', name: 'Billund' },
        { '@type': 'City', name: 'Vejen' },
      ],
      description: customDescription || pageMeta.description || 'Erfaren malermester i Trekantsområdet. Komplette malerløsninger til private og erhverv i Vejle, Kolding, Fredericia og omegn.',
      offers: {
        '@type': 'Offer',
        priceCurrency: 'DKK',
        availability: 'https://schema.org/InStock',
        url: liveUrl,
        seller: {
          '@id': `${LIVE_DOMAIN}/#organization`,
        },
      },
    });
  }

  // Add CaseStudy / Article schema for customer cases
  if (['/normal', '/wine-mage', '/formula'].includes(currentPath) || pageMeta.caseName) {
    const caseName = pageMeta.caseName || customTitle || 'Malerarbejde Kundecase';
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${liveUrl}#casestudy`,
      headline: customTitle || pageMeta.title,
      name: caseName,
      description: customDescription || pageMeta.description || '',
      image: pageMeta.ogImage ? {
        '@type': 'ImageObject',
        url: getOptimizedImageUrl(pageMeta.ogImage, { width: 1200 }),
        width: 1200,
        height: 630,
        caption: caseName,
      } : undefined,
      author: {
        '@id': `${LIVE_DOMAIN}/#organization`,
      },
      publisher: {
        '@id': `${LIVE_DOMAIN}/#organization`,
      },
      mainEntityOfPage: liveUrl,
      inLanguage: 'da-DK',
    });
  }

  // Add FAQPage schema if FAQ items are defined for the page or passed in
  const faqList = customFaq || pageMeta.faq;
  if (faqList && faqList.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: 'da-DK',
      mainEntity: faqList.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return schemas;
}
